import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc, query, where, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';

export interface User {
  uid: string;
  email: string;
  tenantId: string;
  role: 'owner' | 'manager' | 'employee';
  isSuperAdmin?: boolean;
}

export interface Invitation {
  id: string;
  email: string;
  userName: string;
  tenantId: string;
  role: 'owner' | 'manager' | 'employee';
  createdAt: any;
  status: 'pending' | 'used';
}

export const getAllUsers = async (): Promise<User[]> => {
  const querySnapshot = await getDocs(collection(db, 'users'));
  return querySnapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() } as User));
};

export const getUsersByTenant = async (tenantId: string): Promise<User[]> => {
  const q = query(collection(db, 'users'), where('tenantId', '==', tenantId));
  try {
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() } as User));
  } catch (error) {
    console.warn('Error fetching users by tenant (possibly permissions):', error);
    return [];
  }
};

export const updateUserRole = async (uid: string, role: string) => {
  await updateDoc(doc(db, 'users', uid), { role });
};

export const updateUser = async (uid: string, data: { name?: string, email?: string }) => {
  await updateDoc(doc(db, 'users', uid), data);
};

export const createInvitation = async (tenantId: string, email: string, userName: string, role: 'owner' | 'manager' | 'employee'): Promise<string> => {
  const uniqueId = Math.random().toString(36).substring(2, 15);
  const inviteId = `${tenantId}_${uniqueId}`;
  const inviteRef = doc(collection(db, 'tenants', tenantId, 'invitations'), inviteId);
  await setDoc(inviteRef, {
    email: email.toLowerCase(),
    userName,
    tenantId,
    role,
    createdAt: serverTimestamp(),
    status: 'pending'
  });
  return inviteId;
};

export const getInvitation = async (inviteId: string): Promise<Invitation | null> => {
  const parts = inviteId.split('_');
  if (parts.length < 2) return null;
  const tenantId = parts[0];
  const inviteDoc = await getDoc(doc(db, 'tenants', tenantId, 'invitations', inviteId));
  if (inviteDoc.exists()) {
    return { id: inviteDoc.id, ...inviteDoc.data() } as Invitation;
  }
  return null;
};

export const consumeInvitation = async (inviteId: string) => {
  const parts = inviteId.split('_');
  if (parts.length < 2) return;
  const tenantId = parts[0];
  await updateDoc(doc(db, 'tenants', tenantId, 'invitations', inviteId), { status: 'used' });
};
